import { GlobalTopBarActions } from '@/components/layout/global-top-bar-actions';
import { cn } from '@/lib/utils';
import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';

type GlobalTopBarSlotState = {
    start: ReactNode;
    breadcrumbs: ReactNode;
    startTrailing: ReactNode;
    center: ReactNode;
    actions: ReactNode;
    /** Replaces the bar's plain bottom border, for example with a colored accent. */
    edgeClassName: string | null;
    setStart: (node: ReactNode) => void;
    setBreadcrumbs: (node: ReactNode) => void;
    setStartTrailing: (node: ReactNode) => void;
    setCenter: (node: ReactNode) => void;
    setActions: (node: ReactNode) => void;
    setEdgeClassName: (className: string | null) => void;
};

const GlobalTopBarSlotContext = createContext<GlobalTopBarSlotState | null>(null);

export function GlobalTopBarProvider({ children }: { children: ReactNode }) {
    const [start, setStart] = useState<ReactNode>(null);
    const [breadcrumbs, setBreadcrumbs] = useState<ReactNode>(null);
    const [startTrailing, setStartTrailing] = useState<ReactNode>(null);
    const [center, setCenter] = useState<ReactNode>(null);
    const [actions, setActions] = useState<ReactNode>(null);
    const [edgeClassName, setEdgeClassName] = useState<string | null>(null);
    const value = useMemo(
        () => ({
            start,
            breadcrumbs,
            startTrailing,
            center,
            actions,
            edgeClassName,
            setStart,
            setBreadcrumbs,
            setStartTrailing,
            setCenter,
            setActions,
            setEdgeClassName,
        }),
        [start, breadcrumbs, startTrailing, center, actions, edgeClassName],
    );

    return <GlobalTopBarSlotContext.Provider value={value}>{children}</GlobalTopBarSlotContext.Provider>;
}

export function useGlobalTopBarSlots() {
    const ctx = useContext(GlobalTopBarSlotContext);
    if (!ctx) {
        throw new Error('useGlobalTopBarSlots must be used within GlobalTopBarProvider');
    }
    return ctx;
}

export function GlobalTopBarChrome() {
    const ctx = useContext(GlobalTopBarSlotContext);
    if (!ctx) {
        return null;
    }

    const { start, breadcrumbs, startTrailing, center, actions, edgeClassName } = ctx;

    // Three columns: the equal 1fr side tracks keep the center slot centered on the bar,
    // while the right track never shrinks below its buttons.
    return (
        <header
            className={cn(
                'grid h-[49px] grid-cols-[minmax(0,1fr)_auto_minmax(max-content,1fr)] items-center gap-4 px-4',
                edgeClassName ?? 'border-b',
            )}
        >
            <div className="flex min-w-0 items-center gap-3">
                {breadcrumbs ?? start}
                {startTrailing}
            </div>
            <div className="flex items-center justify-center">{center}</div>
            <div className="flex justify-end">
                <GlobalTopBarActions>{actions}</GlobalTopBarActions>
            </div>
        </header>
    );
}
